import openai from '../lib/openaiClient';
import { supabase } from '../lib/supabase';

/**
 * AI Chat Service for Smart Cities Knowledge Base
 * Provides RAG (Retrieval Augmented Generation) capabilities
 */

/**
 * Search knowledge base for relevant context
 * @param {string} query - User's question
 * @returns {Promise<Array>} Relevant knowledge base articles
 */
export async function searchKnowledgeBase(query) {
  try {
    // For now, use text search - can be enhanced with vector search later
    const { data: knowledgeArticles, error } = await supabase
      ?.from('knowledge_base')
      ?.select(`
        id,
        title,
        content,
        summary,
        category_id,
        knowledge_categories!inner(name)
      `)
      ?.eq('is_public', true)
      ?.eq('is_approved', true)
      ?.or(`title.ilike.%${query}%,content.ilike.%${query}%,summary.ilike.%${query}%`)
      ?.limit(5);

    if (error) throw error;
    return knowledgeArticles || [];
  } catch (error) {
    console.error('Error searching knowledge base:', error);
    return [];
  }
}

/**
 * Generate AI response using GPT-5 with RAG context
 * @param {string} userMessage - User's message
 * @param {Array} knowledgeContext - Relevant knowledge base articles
 * @returns {Promise<Object>} AI response with sources
 */
export async function generateSmartCityResponse(userMessage, knowledgeContext = []) {
  try {
    // Build context from knowledge base
    const contextText = knowledgeContext
      ?.map(article => `
Title: ${article?.title}
Category: ${article?.knowledge_categories?.name}
Content: ${article?.content?.substring(0, 1000)}...
      `)
      ?.join('\n---\n') || '';

    const systemPrompt = `You are a Smart Cities AI Assistant specialized in urban planning, smart city technologies, and municipal governance. You help city officials, urban planners, and citizens with questions about smart city initiatives.

CONTEXT from Knowledge Base:
${contextText}

Guidelines:
- Provide practical, actionable advice for smart city implementation
- Reference the knowledge base context when relevant
- If the question is outside smart cities scope, politely redirect to urban planning topics
- Include specific examples and best practices when possible
- Keep responses professional but accessible
- If you don't know something specific, suggest consulting with urban planning experts

Focus areas include:
- Smart transportation and traffic management
- Sustainable energy and utilities
- IoT sensors and data analytics
- Digital governance and citizen services
- Urban sustainability and environment
- Public safety and emergency management`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o', // Using available model instead of gpt-5
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userMessage },
      ],
      max_tokens: 1000,
      temperature: 0.7,
    });

    const aiResponse = response?.choices?.[0]?.message?.content || 'Sorry, I could not generate a response at this time.';

    return {
      response: aiResponse,
      sources: knowledgeContext?.map(article => ({
        id: article?.id,
        title: article?.title,
        category: article?.knowledge_categories?.name,
      })) || [],
      hasKnowledgeContext: knowledgeContext?.length > 0,
    };
  } catch (error) {
    console.error('Error generating AI response:', error);
    throw new Error('Failed to generate AI response. Please try again.');
  }
}

/**
 * Save chat session to database
 * @param {string} userId - User ID
 * @param {string} title - Chat session title
 * @returns {Promise<Object>} Created chat session
 */
export async function createChatSession(userId, title = 'Smart Cities Chat') {
  try {
    const { data: session, error } = await supabase
      ?.from('ai_chat_sessions')
      ?.insert({
        user_id: userId,
        session_title: title,
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return session;
  } catch (error) {
    console.error('Error creating chat session:', error);
    throw error;
  }
}

/**
 * Save chat message to database
 * @param {string} sessionId - Chat session ID
 * @param {string} role - Message role (user/assistant)
 * @param {string} content - Message content
 * @param {Array} referencedKnowledgeIds - Referenced knowledge base IDs
 * @returns {Promise<Object>} Created message
 */
export async function saveChatMessage(sessionId, role, content, referencedKnowledgeIds = []) {
  try {
    const { data: message, error } = await supabase
      ?.from('ai_chat_messages')
      ?.insert({
        session_id: sessionId,
        role,
        content,
        referenced_knowledge_ids: referencedKnowledgeIds?.length > 0 ? referencedKnowledgeIds : null,
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return message;
  } catch (error) {
    console.error('Error saving chat message:', error);
    throw error;
  }
}

/**
 * Get chat history for a session
 * @param {string} sessionId - Chat session ID
 * @returns {Promise<Array>} Chat messages
 */
export async function getChatHistory(sessionId) {
  try {
    const { data: messages, error } = await supabase
      ?.from('ai_chat_messages')
      ?.select('*')
      ?.eq('session_id', sessionId)
      ?.order('created_at', { ascending: true });

    if (error) throw error;
    return messages || [];
  } catch (error) {
    console.error('Error getting chat history:', error);
    return [];
  }
}

/**
 * Get user's chat sessions
 * @param {string} userId - User ID
 * @returns {Promise<Array>} User's chat sessions
 */
export async function getUserChatSessions(userId) {
  try {
    const { data: sessions, error } = await supabase
      ?.from('ai_chat_sessions')
      ?.select('id, session_title, created_at, updated_at')
      ?.eq('user_id', userId)
      ?.order('updated_at', { ascending: false });

    if (error) throw error;
    return sessions || [];
  } catch (error) {
    console.error('Error getting chat sessions:', error);
    return [];
  }
}

/**
 * Complete AI chat workflow - search, generate, and save
 * @param {string} userMessage - User's message
 * @param {string} sessionId - Chat session ID
 * @returns {Promise<Object>} Complete response with AI answer and sources
 */
export async function processAIChat(userMessage, sessionId) {
  try {
    // Step 1: Search knowledge base for relevant context
    const knowledgeContext = await searchKnowledgeBase(userMessage);
    
    // Step 2: Generate AI response with context
    const aiResult = await generateSmartCityResponse(userMessage, knowledgeContext);
    
    // Step 3: Save user message
    await saveChatMessage(sessionId, 'user', userMessage);
    
    // Step 4: Save AI response with knowledge references
    const referencedIds = aiResult?.sources?.map(source => source?.id) || [];
    await saveChatMessage(sessionId, 'assistant', aiResult?.response, referencedIds);
    
    return {
      response: aiResult?.response,
      sources: aiResult?.sources,
      hasKnowledgeContext: aiResult?.hasKnowledgeContext,
    };
  } catch (error) {
    console.error('Error in AI chat workflow:', error);
    throw error;
  }
}